import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateLoanActivity } from '@/hooks/lib/invalidate-loan-activity';
import { api } from '@/lib/api';
import type { FeeCharge, FeeFrequency, FeeSchedule, FeeSummary, FeeType } from '@/types';

export interface CreateFeeSchedulePayload {
  feeType: FeeType;
  name: string;
  amount: number;
  frequency: FeeFrequency;
  autoCharge: boolean;
  notes?: string;
  nextDueDate?: string;
}

function unwrapListResponse<T>(
  payload: T[] | { data?: T[] | null; items?: T[] | null; results?: T[] | null } | null | undefined,
): T[] {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  if (Array.isArray(payload.items)) {
    return payload.items;
  }

  if (Array.isArray(payload.results)) {
    return payload.results;
  }

  return [];
}

function unwrapSummaryResponse(
  payload: FeeSummary | { data?: FeeSummary | null } | null | undefined,
): FeeSummary | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if ('totalFees' in payload) {
    return payload as FeeSummary;
  }

  if (payload.data && typeof payload.data === 'object' && 'totalFees' in payload.data) {
    return payload.data;
  }

  return null;
}

function invalidateLoanAnalyticsQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  loanId: string,
) {
  queryClient.invalidateQueries({ queryKey: ['loans', loanId], exact: true });
  queryClient.invalidateQueries({ queryKey: ['fees', loanId] });
  queryClient.invalidateQueries({ queryKey: ['fee-charges', loanId] });
  queryClient.invalidateQueries({ queryKey: ['fee-summary', loanId] });
  queryClient.invalidateQueries({ queryKey: ['loans', loanId, 'cashflows'], exact: true });
  queryClient.invalidateQueries({ queryKey: ['loans', loanId, 'npv'] });
  queryClient.invalidateQueries({ queryKey: ['loans', loanId, 'yield'], exact: true });
  invalidateLoanActivity(queryClient, loanId);
}

export function useFeeSchedules(loanId: string) {
  return useQuery<FeeSchedule[]>({
    queryKey: ['fees', loanId],
    queryFn: async () => {
      const res = await api.get(`/loans/${loanId}/fees`);
      return unwrapListResponse<FeeSchedule>(res.data);
    },
    enabled: !!loanId,
  });
}

export function useCreateFeeSchedule(loanId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateFeeSchedulePayload) => {
      const res = await api.post(`/loans/${loanId}/fees`, data);
      return res.data;
    },
    onSuccess: () => {
      invalidateLoanAnalyticsQueries(queryClient, loanId);
    },
  });
}

export function useWaiveFee(loanId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ feeId, reason }: { feeId: string; reason: string }) => {
      const res = await api.post(`/loans/${loanId}/fees/${feeId}/waive`, { reason });
      return res.data;
    },
    onSuccess: () => {
      invalidateLoanAnalyticsQueries(queryClient, loanId);
    },
  });
}

export function useFeeCharges(loanId: string) {
  return useQuery<FeeCharge[]>({
    queryKey: ['fee-charges', loanId],
    queryFn: async () => {
      const res = await api.get(`/loans/${loanId}/fees/charges`);
      return unwrapListResponse<FeeCharge>(res.data);
    },
    enabled: !!loanId,
  });
}

export function useGenerateRecurringFees(loanId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post(`/loans/${loanId}/fees/generate-recurring`);
      return res.data;
    },
    onSuccess: () => {
      invalidateLoanAnalyticsQueries(queryClient, loanId);
    },
  });
}

export function useChargeFee(loanId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (chargeId: string) => {
      const res = await api.post(`/loans/${loanId}/fees/charges/${chargeId}/charge`);
      return res.data;
    },
    onSuccess: () => {
      invalidateLoanAnalyticsQueries(queryClient, loanId);
    },
  });
}

export function usePayFeeCharge(loanId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (chargeId: string) => {
      const res = await api.post(`/loans/${loanId}/fees/charges/${chargeId}/pay`);
      return res.data;
    },
    onSuccess: () => {
      invalidateLoanAnalyticsQueries(queryClient, loanId);
    },
  });
}

export function useWaiveFeeCharge(loanId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ chargeId, reason }: { chargeId: string; reason: string }) => {
      const res = await api.post(`/loans/${loanId}/fees/charges/${chargeId}/waive`, { reason });
      return res.data;
    },
    onSuccess: () => {
      invalidateLoanAnalyticsQueries(queryClient, loanId);
    },
  });
}

export function useFeeSummary(loanId: string) {
  return useQuery<FeeSummary>({
    queryKey: ['fee-summary', loanId],
    queryFn: async () => {
      const res = await api.get(`/loans/${loanId}/fees/summary`);
      return (
        unwrapSummaryResponse(res.data) ?? {
          totalFees: 0,
          activeFees: 0,
          totalPaid: 0,
          totalOutstanding: 0,
          totalWaived: 0,
          outstandingCharges: 0,
          feeCount: 0,
          chargeCount: 0,
        }
      );
    },
    enabled: !!loanId,
  });
}
